import IMask, { InputMask, MaskElement } from 'imask'
import {
  ChangeEvent,
  RefObject,
  useEffect,
  useRef,
  MutableRefObject,
} from 'react'

export type UseIMaskReturnValue<
  Mask extends IMask.AnyMaskedOptions,
  Element extends HTMLElement | MaskElement = HTMLInputElement,
> = {
  reference: RefObject<Element>
  maskReference?: RefObject<InputMask<Mask> | undefined>
}

export type IMaskHandlers<
  Mask extends IMask.AnyMaskedOptions,
  Element extends HTMLElement | MaskElement = HTMLInputElement,
> = {
  onAccept?: (
    event: ChangeEvent<Element>,
    mask: InputMask<Mask> | undefined,
  ) => void
}

export function useIMask<
  Mask extends IMask.AnyMaskedOptions,
  Element extends HTMLElement | MaskElement = HTMLInputElement,
>(
  options: Mask,
  { onAccept }: IMaskHandlers<Mask, Element> = {},
): UseIMaskReturnValue<Mask, Element> {
  const reference = useRef<Element>(null)
  const maskReference = useRef<InputMask<Mask> | undefined>()
  const acceptReference = useRef(onAccept)

  function handleChangeMaskEvent(
    handleReference: MutableRefObject<
      (event: ChangeEvent<Element>, mask: IMask.InputMask<Mask>) => void
    >,
  ) {
    if (!maskReference.current && !handleReference.current) {
      return
    }

    maskReference?.current?.on('accept', (event: ChangeEvent<Element>) => {
      handleReference.current?.(event, maskReference.current)
    })
  }

  useEffect(() => {
    acceptReference.current = onAccept
  }, [onAccept])

  useEffect(() => {
    if (!reference.current) {
      return
    }
    if (!maskReference.current) {
      maskReference.current = IMask(reference.current, options)
    }
    maskReference.current?.updateValue()
    maskReference.current?.updateOptions(options)
    handleChangeMaskEvent(acceptReference)
  }, [options])

  return { reference, maskReference }
}
